import { wrapper, createApiClient, Result, ApiError } from "../base";
import RuleModelDTO = App.Data.RuleEngine.RuleModelDTO;
import RuleModelVersionDTO = App.Data.RuleEngine.RuleModelVersionDTO;
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import RuleModelMetaDTO = App.Data.RuleEngine.RuleModelMetaDTO;
import VehicleDTO = App.Data.VehicleDTO;
import RuleModelVersionMinimalDTO = App.Data.RuleEngine.RuleModelVersionMinimalDTO;
import RuleModelDirectActionValuesDTO = App.Data.RuleEngine.RuleModelDirectActionValuesDTO;

const baseClient = createApiClient();

type GetAvailableCarsParams = {
	"filter[vehicle_city]"?: string;
	"filter[vehicle_brand_model]"?: string;
};

export default class {
	index(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
	}): Promise<Result<RuleModelDTO[], ApiError>> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get("rule-engine/model", {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}

	showModel(ruleModelUuid: string): Promise<Result<RuleModelDTO, ApiError>> {
		return wrapper(baseClient.get(`rule-engine/model/${ruleModelUuid}`));
	}
	showModelByName(name: string): Promise<Result<RuleModelDTO, ApiError>> {
		return wrapper(baseClient.get(`rule-engine/model/name/${name}`));
	}

	showModelMeta(
		ruleModelUuid: string
	): Promise<Result<RuleModelMetaDTO, ApiError>> {
		return wrapper(baseClient.get(`rule-engine/model/${ruleModelUuid}/meta`));
	}

	getFilterValues() {
		return wrapper(baseClient.get(`pricing-engine/compare/filter-values`));
	}

	getMyWheelsAvailableCars(params?: GetAvailableCarsParams): Promise<
		Result<
			{
				vehicles: VehicleDTO[];
				models: { vehicle_brand_model: string }[];
				cities: { vehicle_city: string }[];
			},
			ApiError
		>
	> {
		return wrapper(
			baseClient.get(`pricing-engine/compare/my-wheels-available-cars`, {
				params,
			})
		);
	}

	versionIndex(
		ruleModelUuid: string,
		params: {
			page: number;
			itemsPerPage: number;
			sortBy: { key: string; order: "asc" | "desc" }[];
		}
	): Promise<Result<RuleModelVersionMinimalDTO[], ApiError>> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get(`rule-engine/model/${ruleModelUuid}/versions`, {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}

	showVersion(
		ruleModelUuid: string,
		ruleModelVersionUuid: string
	): Promise<Result<RuleModelVersionDTO, ApiError>> {
		return wrapper(
			baseClient.get(
				`rule-engine/model/${ruleModelUuid}/versions/${ruleModelVersionUuid}`
			)
		);
	}

	makeActiveVersion(
		ruleModelUuid: string,
		ruleModelVersionUuid: string
	): Promise<Result<string[], ApiError>> {
		return wrapper(
			baseClient.patch(
				`rule-engine/model/${ruleModelUuid}/versions/${ruleModelVersionUuid}/make-active-version`
			)
		);
	}

	destroyVersion(
		ruleModelUuid: string,
		ruleModelVersionUuid: string
	): Promise<Result<string[], ApiError>> {
		return wrapper(
			baseClient.delete(
				`rule-engine/model/${ruleModelUuid}/versions/${ruleModelVersionUuid}`
			)
		);
	}

	destroyVersions(
		ruleModelUuid: string,
		selectedRuleModelVersionUuids: string[]
	): Promise<Result<string[], ApiError>> {
		return wrapper(
			baseClient.delete(
				`rule-engine/model/${ruleModelUuid}/versions/selection`,
				{
					params: {
						selectedRuleModelVersionUuids,
					},
				}
			)
		);
	}

	storeVersion(
		ruleModelVersion: RuleModelVersionDTO,
		ruleModelUuid: string,
		saveStrategy: "new" | "replace"
	): Promise<Result<RuleModelVersionDTO, ApiError>> {
		return wrapper(
			baseClient.put(
				`rule-engine/model/${ruleModelUuid}/versions/${saveStrategy}`,
				ruleModelVersion
			)
		);
	}

	introspectVehiclePricing(
		vehicleUuid: string,
		requestBody: object
	): Promise<Result<RuleModelMetaDTO, ApiError>> {
		return wrapper(
			baseClient.post(
				`pricing-engine/compare/pricing/vehicles/${vehicleUuid}/price/introspect`,
				requestBody
			)
		);
	}

	introspectManyVehiclePricesAsAverage(
		requestBody: object
	): Promise<Result<RuleModelMetaDTO, ApiError>> {
		return wrapper(
			baseClient.post(
				`pricing-engine/compare/pricing/vehicles/price/introspect-many-as-average`,
				requestBody
			)
		);
	}

	async getDimensions(): Promise<Result<RuleDimension[], ApiError>> {
		return wrapper(baseClient.get(`rule-engine/dimensions`));
	}

	async getDimensionValues(
		dimensionUuid: string
	): Promise<
		Result<{ display_name: string; value: string | null }[], ApiError>
	> {
		return wrapper(
			baseClient.get(`rule-engine/dimensions/${dimensionUuid}/values`)
		);
	}

	async getHierarchyDefinitions(
		dimensionUuid: string
	): Promise<Result<App.Data.Hierarchy.HierarchyDefinitionDO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`rule-engine/dimensions/${dimensionUuid}/hierarchy-definitions`
			)
		);
	}

	async getHierarchyTree(
		dimensionUuid: string,
		hierarchyDefinitionId: number
	): Promise<
		Result<App.Data.Explorer.Filter.QuickFilterNodeDTO[], ApiError>
	> {
		return wrapper(
			baseClient.get(
				`rule-engine/dimensions/${dimensionUuid}/hierarchy-definitions/${hierarchyDefinitionId}/tree`
			)
		);
	}

	async getDirectActionValues(
		actionType: string
	): Promise<Result<RuleModelDirectActionValuesDTO, ApiError>> {
		return wrapper(
			baseClient.get(`rule-engine/direct-action-values/${actionType}`)
		);
	}
}
